import * as React from "react";

import "./ComponentPage.scss";

import ComponentApiContent from "./ComponentApiContent";
import ComponentExamplesContent from "./ComponentExamplesContent";
import { IExample } from "./Examples.Types";
import { IPageDefinitionSteps } from "./PageDefinitionTypes";

import { Button } from "azure-devops-ui/Button";
import { Link } from "azure-devops-ui/Link";
import { IStatusProps, Status, Statuses } from "azure-devops-ui/Status";
import SassDocContent from "./SassDocContent";

export interface IComponentInternalLinks {
    figma?: string;
    github?: string;
}

export interface IComponentPageProps {
    anatomy?: React.ComponentType;
    apiSources?: string[];
    devGuidance?: React.ComponentType;
    description?: string;
    examples?: IExample[];
    internalLinks?: IComponentInternalLinks;
    name: string;
    status?: IPageDefinitionSteps;
    styleDocGroups?: string[];
    usage?: React.ComponentType;
}

class ComponentPage extends React.Component<IComponentPageProps> {
    public render() {
        const Anatomy = this.props.anatomy!;
        const Usage = this.props.usage!;
        const DevGuidance = this.props.devGuidance!;
        return (
            <div className="component-page">
                <div className="flex-row flex-baseline">
                    <h1 className="page-title">{this.props.name}</h1>
                    <Status {...this.getComponentStatus()} />
                </div>
                {this.props.description && <div className="feature">{this.props.description}</div>}
                {this.props.examples && (
                    <>
                        <h2 className="component-section-header">Examples</h2>
                        <ComponentExamplesContent examples={this.props.examples} />{" "}
                    </>
                )}
                {this.renderInternalLinks()}
                {this.renderTableOfContents()}
                {this.props.usage && (
                    <>
                        <h2 className="component-section-header" id="usage">
                            Usage
                        </h2>
                        <div className="component-usage markdown-content">
                            <Usage />
                        </div>
                    </>
                )}
                {this.props.anatomy && (
                    <>
                        <h2 className="component-section-header" id="anatomy">
                            Anatomy
                        </h2>
                        <div className="component-anatomy markdown-content">
                            <Anatomy />
                        </div>
                    </>
                )}
                {this.props.devGuidance && (
                    <>
                        <h2 className="component-section-header" id="dev-guidance">
                            Developer guidance
                        </h2>
                        <div className="component-dev-guidance markdown-content">
                            <DevGuidance />
                        </div>
                    </>
                )}
                {this.props.apiSources && (
                    <>
                        <h2 className="component-section-header" id="api">
                            APIs
                        </h2>
                        <ComponentApiContent sources={this.props.apiSources} />
                    </>
                )}
                {this.props.styleDocGroups && (
                    <>
                        <a id="styles">
                            <h2 className="component-section-header">Styles</h2>
                        </a>
                        <SassDocContent groups={this.props.styleDocGroups} />
                    </>
                )}
            </div>
        );
    }

    private renderInternalLinks(): JSX.Element | null {
        if (this.props.internalLinks) {
            if (this.props.internalLinks.figma || this.props.internalLinks.github) {
                return (
                    <div className="component-internal-links">
                        {this.props.internalLinks && this.props.internalLinks.figma && (
                            <Button
                                className="component-header-link"
                                href={this.props.internalLinks.figma}
                                target="_blank"
                                text="Open in Figma"
                                iconProps={{
                                    render: () => <img alt="" className="custom-button-icon" src={require("../Images/figma.svg")} />
                                }}
                            />
                        )}
                        {this.props.internalLinks && this.props.internalLinks.github && (
                            <Button
                                className="component-header-link"
                                href={this.props.internalLinks.github}
                                target="_blank"
                                text="GitHub issue"
                                iconProps={{
                                    render: () => <img alt="" className="custom-button-icon hc-invert-dark" src={require("../Images/github.svg")} />
                                }}
                            />
                        )}
                    </div>
                );
            }
        }

        return null;
    }

    private renderTableOfContents(): JSX.Element | null {
        return this.props.usage || this.props.anatomy || this.props.apiSources ? (
            <div className="component-sections-header flex-column flex-start">
                <h2 className="component-section-header">Contents</h2>
                {this.props.usage && (
                    <Link className="component-section-link" href="#usage" underline>
                        Usage
                    </Link>
                )}
                {this.props.anatomy && (
                    <Link className="component-section-link" href="#anatomy" underline>
                        Anatomy
                    </Link>
                )}
                {this.props.devGuidance && (
                    <Link className="component-section-link" href="#dev-guidance" underline>
                        Developer guidance
                    </Link>
                )}
                {this.props.apiSources && (
                    <Link className="component-section-link" href="#api" underline>
                        APIs
                    </Link>
                )}
                {this.props.styleDocGroups && (
                    <Link className="component-section-link" href="#styles" underline>
                        Styles
                    </Link>
                )}
            </div>
        ) : null;
    }

    private getComponentStatus(): IStatusProps {
        let codeStatus: string | undefined;
        if (this.props.status) {
            codeStatus = this.props.status.code;
        }

        if (codeStatus === "done") {
            return { ...Statuses.Success, className: "component-page-header-status" };
        } else if (codeStatus === "deprecated") {
            return { ...Statuses.Warning, className: "component-page-header-status" };
        } else if (codeStatus === "underReview") {
            return { ...Statuses.Warning, className: "component-page-header-status" };
        } else {
            return { ...Statuses.Waiting, className: "component-page-header-status" };
        }
    }
}

export default ComponentPage;
